import * as Cookies from 'es-cookie';

interface ClientStorageOptions {
  daysUntilExpire?: number;
  cookieDomain?: string;
}

/**
 * Defines a type that handles storage to/from a storage location
 */
export type ClientStorage = {
  get<T extends Object>(key: string): T | undefined;
  save(key: string, value: any, options?: ClientStorageOptions): void;
  remove(key: string, options?: ClientStorageOptions): void;
};

/**
 * A storage protocol for marshalling data to/from cookies
 */

function deserialize<T>(value: unknown) {
  if (typeof value === 'string') {
    try {
      return <T>JSON.parse(value);
    } catch (e) {}
    return <T>value;
  }
  return undefined;
}

function serialize<T>(value: T): string {
  if (typeof value === 'string') {
    return value;
  }
  return JSON.stringify(value);
}

export const CookieStorage = {
  get<T extends Object>(key: string) {
    const value = Cookies.get(key);

    if (value == null) {
      return;
    }

    return deserialize<T>(value);
  },

  save(key: string, value: any, options?: ClientStorageOptions): void {
    if (value == null) {
      Cookies.remove(key);
      return;
    }
    let cookieAttributes: Cookies.CookieAttributes = {};

    if ('https:' === window.location.protocol) {
      cookieAttributes = {
        secure: true,
        sameSite: 'strict'
      };
    }

    if (options?.daysUntilExpire) {
      cookieAttributes.expires = options.daysUntilExpire;
    }

    if (options?.cookieDomain) {
      cookieAttributes.domain = options.cookieDomain;
    }

    Cookies.set(key, serialize(value), cookieAttributes);
  },

  remove(key: string, options?: ClientStorageOptions) {
    let cookieAttributes: Cookies.CookieAttributes = {};

    if (options?.cookieDomain) {
      cookieAttributes.domain = options.cookieDomain;
    }

    Cookies.remove(key, cookieAttributes);
  }
} as ClientStorage;

/**
 * Cookie storage that creates a cookie for modern and legacy browsers.
 * See: https://web.dev/samesite-cookie-recipes/#handling-incompatible-clients
 */

/**
 * A storage protocol for marshalling data to/from session storage
 */
export const SessionStorage = {
  get<T extends Object>(key: string) {
    /* c8 ignore next 3 */
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    const value = sessionStorage.getItem(key);

    if (value == null) {
      return;
    }

    return deserialize<T>(value);
  },

  save(key: string, value: any): void {
    if (value == null) {
      SessionStorage.remove(key);
      return;
    }
    sessionStorage.setItem(key, serialize(value));
  },

  remove(key: string) {
    sessionStorage.removeItem(key);
  }
} as ClientStorage;

export const LocalStorage = {
  get<T extends Object>(key: string) {
    /* c8 ignore next 3 */
    if (typeof localStorage === 'undefined') {
      return;
    }

    const value = localStorage.getItem(key);

    if (value == null) {
      return;
    }

    return deserialize<T>(value);
  },

  save(key: string, value: any): void {
    if (value == null) {
      LocalStorage.remove(key);
      return;
    }
    localStorage.setItem(key, serialize(value));
  },

  remove(key: string) {
    localStorage.removeItem(key);
  }
} as ClientStorage;